import jwtDecode from "jwt-decode";

export const ROLE_CLAIM =
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

function getAccessToken() {
  return (
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken") ||
    ""
  );
}

/**
 * @returns {Record<string, unknown> | null}
 */
export function getDecodedAccessToken() {
  const token = getAccessToken();
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
}

/**
 * Prefer JWT claims (source of truth), then stored user details.
 * @returns {string}
 */
function firstRole(value) {
  if (Array.isArray(value)) {
    const admin = value.find((r) => String(r).trim().toLowerCase() === "admin");
    return String(admin || value[0] || "").trim();
  }
  return value ? String(value).trim() : "";
}

export function getUserRoles() {
  const decoded = getDecodedAccessToken();
  if (decoded) {
    const fromToken =
      decoded.RoleName ||
      decoded[ROLE_CLAIM] ||
      decoded.role;
    if (Array.isArray(fromToken)) {
      return fromToken.map((r) => String(r).trim()).filter(Boolean);
    }
    if (fromToken) return [String(fromToken).trim()];
  }

  const single = getUserRole();
  return single ? [single] : [];
}

export function getUserRole() {
  const decoded = getDecodedAccessToken();
  if (decoded) {
    const fromToken =
      decoded.RoleName ||
      decoded[ROLE_CLAIM] ||
      decoded.role;
    const picked = firstRole(fromToken);
    if (picked) return picked;
  }

  try {
    const raw = localStorage.getItem("sundorbonBookingUserDetails");
    if (raw) {
      const type = JSON.parse(raw)?.Type;
      if (type) return String(type).trim();
    }
  } catch {
    // ignore
  }

  return "";
}

/**
 * @param {string} [role]
 */
export function isAdminRole(role) {
  if (role != null && role !== "") {
    return role.toString().trim().toLowerCase() === "admin";
  }
  return getUserRoles().some((r) => r.toLowerCase() === "admin");
}

export function isAdminUser() {
  return getUserRoles().some((r) => r.toLowerCase() === "admin");
}

/**
 * @param {string} [role]
 */
export function isCustomerUserRole(role) {
  const normalized = (role ?? getUserRole()).toString().trim().toLowerCase();
  return normalized === "user";
}

export function isCustomerUser() {
  return isCustomerUserRole(getUserRole());
}

/** Admin or User (customer) — both use SignalR hub groups. */
export function usesSignalRNotifications() {
  return isAdminUser() || isCustomerUser();
}

/**
 * User id for SignalR groups and notification APIs.
 * @returns {string}
 */
export function getSignalRUserId() {
  try {
    const raw = localStorage.getItem("sundorbonBookingUserDetails");
    if (raw) {
      const id = JSON.parse(raw)?.Id;
      if (id != null) return String(id).trim();
    }
  } catch {
    // fall through
  }

  const decoded = getDecodedAccessToken();
  if (decoded?.UserId) return String(decoded.UserId).trim();
  return "";
}
